import { ChevronLeftIcon, ChevronRightIcon } from '@chakra-ui/icons';
import { Button, Flex, HStack, IconButton, StackProps, Text } from '@chakra-ui/react';
import { PageSizeSelector } from './PageSizeSelector';
import { listPaginationControlMessages as messages } from './messages';
import { ELLIPSIS } from './utils/getPagesOptions/ellipsis';
import { useListPagination } from './utils/useListPagination';
import { ChangeEventHandler } from 'react';
import { ResponseMeta } from '@/model';

export interface ListPaginationControlProps extends StackProps {
  currentPage: number;
  totalPages: number;
  onPageChange?: (page: number) => void;
  pageSize?: number;
  onPageSizeChange?: (itemsPerPage: number) => void;
  meta?: ResponseMeta;
}

export const ListPaginationControl = ({
  currentPage,
  totalPages,
  onPageChange,
  pageSize,
  onPageSizeChange,
  meta,
  ...stackProps
}: ListPaginationControlProps): JSX.Element | null => {
  const { hasNextPage, hasPreviousPage, nextPage, previousPage, pagesOptions } = useListPagination({ currentPage, totalPages });

  const handleChangePage = (page: number) => () => onPageChange?.(page);

  const handlePageSizeChange: ChangeEventHandler<HTMLSelectElement> = event => onPageSizeChange?.(Number(event.target.value));

  const startItem = (currentPage - 1) * (meta?.perpage || 0) + 1;
  const endItem = Math.min(currentPage * (meta?.perpage || 0), meta?.total || 0);

  return (
    <Flex flexWrap='wrap' gap='deka'>
      <Flex direction='row' alignItems='center' gap={4}>
        <PageSizeSelector value={pageSize} onChange={handlePageSizeChange} />
        {meta && (
          <>
            <div className='separator'></div>
            <Text>{`${startItem}–${endItem} de ${meta.total || 0} itens`}</Text>
          </>
        )}
      </Flex>

      <HStack alignItems='center' justifyContent='end' spacing='deka' flexGrow={1} {...stackProps}>
        <IconButton
          aria-label={messages.previousPage}
          icon={<ChevronLeftIcon boxSize={5} color='gray.800' />}
          isDisabled={!hasPreviousPage}
          onClick={handleChangePage(previousPage)}
          variant='ghost'
        />

        <HStack>
          {pagesOptions.map((pageOption, index) => {
            const isEllipsis = pageOption === ELLIPSIS;
            const isCurrentPage = pageOption === currentPage;

            return (
              <Button
                key={`${pageOption}-${index}`}
                variant={isCurrentPage ? 'solid' : 'ghost'}
                size='xs'
                colorScheme={isCurrentPage ? 'brand' : 'neutral'}
                px='nano'
                borderRadius='lg'
                isDisabled={isEllipsis}
                onClick={!isEllipsis ? handleChangePage(Number(pageOption)) : undefined}
              >
                {pageOption}
              </Button>
            );
          })}
        </HStack>

        <IconButton
          aria-label={messages.nextPage}
          icon={<ChevronRightIcon boxSize={5} color='gray.800' />}
          isDisabled={!hasNextPage}
          onClick={handleChangePage(nextPage)}
          variant='ghost'
        />
      </HStack>
    </Flex>
  );
};
